class FlixHeader extends HTMLElement {
  connectedCallback() {
    // One signal for every listener this element puts on the document, so
    // leaving the page — which the Theme Editor does on every header edit
    // — takes all of them with it instead of stacking a set per render.
    this._teardown = new AbortController();
    this._initGlass();
    this._initFit();
    this._initMobileNav();
    this._initMegaMenu();
    this._initCartOpen();
    this._initSearchOpen();
    this._initAccountState();
  }

  _initGlass() {
    const sentinel = document.querySelector('[data-flix-sentinel]');
    if (!sentinel || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(
      (entries) => {
        const glass = !entries[0].isIntersecting;
        this.setAttribute('data-glass', glass ? 'true' : 'false');
      },
      { threshold: 0 }
    );
    io.observe(sentinel);
    this._io = io;
  }

  disconnectedCallback() {
    if (this._teardown) this._teardown.abort();
    if (this._io) this._io.disconnect();
    if (this._fitObserver) this._fitObserver.disconnect();
  }

  // The bar's one row — brand, menu, actions — is as wide as the menu's
  // links and the language make it, and nothing made it fit: on a window
  // narrower than the row the actions were pushed out past the bar's edge.
  // The row is tightened a step at a time (the steps are in theme-v5.css,
  // under "Header: one row that has to fit") until it sits inside the bar.
  //
  // The measuring itself is window.FlixHeaderFit, written inline right after
  // the bar in sections/header.liquid so that it has already run once before
  // the page is first painted. Here it is run again whenever the bar's width
  // changes.
  _initFit() {
    const fit = () => {
      if (typeof window.FlixHeaderFit === 'function') window.FlixHeaderFit(this);
    };
    fit();
    if ('ResizeObserver' in window) {
      // Only the bar's outer width (the window's) calls for a new measurement.
      // The steps themselves change its padding, and so its inner width;
      // answering that too measured again after every step.
      let width = -1;
      this._fitObserver = new ResizeObserver(() => {
        const now = Math.round(this.getBoundingClientRect().width);
        if (now === width) return;
        width = now;
        fit();
      });
      this._fitObserver.observe(this);
    } else {
      window.addEventListener('resize', fit, { signal: this._teardown.signal });
    }
  }

  _initMobileNav() {
    const burger = this.querySelector('[data-flix-menu-toggle]');
    const panel = this.querySelector('[data-flix-mobile-nav]');
    if (!burger || !panel) return;
    const close = () => {
      panel.removeAttribute('data-open');
      burger.setAttribute('aria-expanded', 'false');
    };
    burger.addEventListener('click', () => {
      const open = panel.getAttribute('data-open') === 'true';
      if (open) close();
      else {
        panel.setAttribute('data-open', 'true');
        burger.setAttribute('aria-expanded', 'true');
        // The room left under the bar: a longer list scrolls inside it.
        panel.style.maxHeight = Math.max(160, window.innerHeight - panel.getBoundingClientRect().top - 8) + 'px';
      }
    });
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) close();
    }, { signal: this._teardown.signal });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') close();
    }, { signal: this._teardown.signal });
  }

  // A menu link that has children opens a small panel under it.
  //   Mouse: while the pointer is over the link or the panel.
  //   Keyboard: when focus arrives, and for as long as it stays inside —
  //   closing on every focusout hid the panel before Tab could reach it.
  //   Touch: there is no hover, and a tap used to follow the link at once,
  //   so the panel was never seen. The first tap opens it; a tap on the
  //   link while it is open follows the link.
  _initMegaMenu() {
    const wraps = [...this.querySelectorAll('.flx-nav > span')].filter((wrap) => wrap.querySelector('.flx-mega'));
    if (!wraps.length) return;
    const isOpen = (wrap) => wrap.querySelector('.flx-mega').style.display === 'block';
    const set = (wrap, open) => {
      wrap.querySelector('.flx-mega').style.display = open ? 'block' : 'none';
      wrap.querySelector(':scope > a').setAttribute('aria-expanded', open ? 'true' : 'false');
    };
    const closeAll = (except) => wraps.forEach((wrap) => { if (wrap !== except) set(wrap, false); });
    wraps.forEach((wrap) => {
      const link = wrap.querySelector(':scope > a');
      let touch = false;
      let openAtPress = false;
      link.setAttribute('aria-haspopup', 'true');
      link.setAttribute('aria-expanded', 'false');
      wrap.addEventListener('mouseenter', () => { closeAll(wrap); set(wrap, true); });
      wrap.addEventListener('mouseleave', () => set(wrap, false));
      wrap.addEventListener('focusin', (e) => {
        if (e.target.matches(':focus-visible')) { closeAll(wrap); set(wrap, true); }
      });
      wrap.addEventListener('focusout', (e) => { if (!wrap.contains(e.relatedTarget)) set(wrap, false); });
      link.addEventListener('pointerdown', (e) => {
        touch = e.pointerType === 'touch' || e.pointerType === 'pen';
        openAtPress = isOpen(wrap);
      });
      link.addEventListener('click', (e) => {
        if (touch && !openAtPress) { e.preventDefault(); closeAll(wrap); set(wrap, true); }
        touch = false;
      });
    });
    document.addEventListener('click', (e) => {
      if (!wraps.some((wrap) => wrap.contains(e.target))) closeAll();
    }, { signal: this._teardown.signal });
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      const open = wraps.find(isOpen);
      if (!open) return;
      set(open, false);
      if (open.contains(document.activeElement)) open.querySelector(':scope > a').focus();
    }, { signal: this._teardown.signal });
  }

  _initCartOpen() {
    document.addEventListener('flix:cart:count', (e) => {
      this.querySelectorAll('[data-flix-cart-count]').forEach((el) => { el.textContent = e.detail.count; });
    }, { signal: this._teardown.signal });
  }

  _initSearchOpen() {
    this.querySelectorAll('[data-flix-search-open]').forEach((btn) => {
      btn.addEventListener('click', () => document.dispatchEvent(new CustomEvent('flix:search:open')));
    });
  }

  // Flix Identity owns sign-in state, not Shopify customer accounts — the
  // icon never branches on Liquid `customer`. It listens for
  // flix:auth:state-changed (dispatched by assets/flix-auth-client.js,
  // which probes GET /me on its own on every page load) rather than
  // calling into FlixAuthClient directly, so this has no dependency on
  // script load order.
  _initAccountState() {
    const icon = this.querySelector('[data-flix-account-icon]');
    if (!icon) return;
    document.addEventListener('flix:auth:state-changed', (e) => {
      icon.setAttribute('data-flix-account-state', e.detail.state);
    }, { signal: this._teardown.signal });
  }
}
customElements.define('flix-header', FlixHeader);
